import { createListenerMiddleware } from "@reduxjs/toolkit";
import { matchPath } from "react-router";
import { CHAT_DEFAULT_WIDTH } from "../utilities/chat";
import type { ChatState } from "./chat";
import type { LocalState } from "./local";
import type { PlaybookState } from "./playbook";

enum Key {
  AppSlug = "appSlug",
  OrgSlug = "orgSlug",
  ViewedGuides = "playbook.viewedGuides",
  ChatWidth = "chat.width",
}

function loadString(key: Key): string | null {
  return localStorage.getItem(key);
}

function loadJSON<T>(key: Key, fallback: T): T {
  const raw = localStorage.getItem(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn("Failed to parse JSON from localStorage", { key, raw, error });
    return fallback;
  }
}

function saveString(key: Key, value: string | null): void {
  if (value) {
    localStorage.setItem(key, value);
  } else {
    localStorage.removeItem(key);
  }
}

function saveJSON(key: Key, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function loadLocal() {
  const match = matchPath(
    { path: "/apps/:appId", caseSensitive: false, end: false },
    window.location.pathname,
  );
  return {
    appSlug: match?.params.appId ?? loadString(Key.AppSlug),
    orgSlug: loadString(Key.OrgSlug),
  } satisfies Partial<LocalState>;
}

export function loadChat() {
  const width = Number(loadString(Key.ChatWidth));
  return {
    width: Number.isFinite(width) && width > 0 ? width : CHAT_DEFAULT_WIDTH,
  } satisfies Partial<ChatState>;
}

export function loadPlaybook() {
  return {
    viewedGuides: loadJSON<PlaybookState["viewedGuides"]>(Key.ViewedGuides, {}),
  } satisfies Partial<PlaybookState>;
}

export const persist = createListenerMiddleware<{
  chat: ChatState;
  local: LocalState;
  playbook: PlaybookState;
}>();

persist.startListening({
  predicate: (_action, current, previous) =>
    current.local.appSlug !== previous.local.appSlug ||
    current.local.orgSlug !== previous.local.orgSlug,
  effect: (_action, api) => {
    const { local } = api.getState();
    saveString(Key.AppSlug, local.appSlug);
    saveString(Key.OrgSlug, local.orgSlug);
  },
});

persist.startListening({
  predicate: (_action, current, previous) =>
    current.playbook.viewedGuides !== previous.playbook.viewedGuides,
  effect: (_action, api) => {
    const { playbook } = api.getState();
    saveJSON(Key.ViewedGuides, playbook.viewedGuides);
  },
});

persist.startListening({
  predicate: (_action, current, previous) =>
    current.chat.width !== previous.chat.width,
  effect: (_action, api) => {
    saveString(Key.ChatWidth, String(api.getState().chat.width));
  },
});
